import React from "react";
import { useSelector } from "react-redux";
import { LocalizationProvider } from "@fluent/react";

import ContainerButton from "@kikoff/components/src/v1/buttons/ContainerButton";
import { web } from "@kikoff/proto/src/protos";
import { combineClasses, format } from "@kikoff/utils/src/string";

import {
  productData,
  useCreditServiceGenericMentalModelName,
} from "@src/constants";
import { l10nResources } from "@util/l10n";

import styles from "./styles.module.scss";

const l10n = l10nResources({
  en: `months-abbr = mo
months = months
for = for
product-entry-month-plan = {$additionalMonths} month plan
lending-product-entry-month-plan = Estimated payback: {$additionalMonths} months
estimated-payment = estimated payment
price-per-month = {$pricePerMonth}/mo for {$additionalMonths} months
`,
  es: `months-abbr = mes
months = meses
for = por
product-entry-month-plan = plan de {$additionalMonths} meses
lending-product-entry-month-plan = Reembolso estimado: {$additionalMonths} meses
estimated-payment = pago estimado
price-per-month = {$pricePerMonth}/mes durante {$additionalMonths} meses
`,
});

interface ProductEntryProps {
  product: web.public_.IProduct;
  onClick?: React.EventHandler<React.SyntheticEvent>;
  className?: string;
  action?: {
    url: string;
    text: string;
  };
  hidePrice?: boolean;
  disabled?: boolean;
  widen?: true;
  contentOnly?: true;
}

const ProductEntry: React.FC<ProductEntryProps> = ({
  product,
  onClick,
  className,
  action,
  widen,
  contentOnly,
  disabled,
}) => {
  const emphasizeLending = useSelector((state) => state.page.emphasizeLending);

  const imageURL = product.images.find(
    (image) => image.type === web.public_.Product.Image.Type.ICON
  )?.url;

  const useStreamlinedSubscriptionTemplate =
    product.type === web.public_.Product.Type.SUBSCRIPTION;

  const subtitle = product.metadata[0]?.value;

  const additionalMonths = product.repaymentTerm;

  const price = format.money(product.priceCents, {
    includeCents: true,
  });

  const productTitle = useCreditServiceGenericMentalModelName();

  const productSubtitle = l10n.getString(
    emphasizeLending
      ? "lending-product-entry-month-plan"
      : "product-entry-month-plan",
    { additionalMonths }
  );

  return (
    <ContainerButton
      className={combineClasses(
        styles["product-entry"],
        className,
        widen && styles.widen,
        contentOnly && styles.contentOnly,
        disabled && styles.disabled,
        onClick && styles.clickable
      )}
      data-cy="product-entry"
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "initial" }}
    >
      <LocalizationProvider l10n={l10n}>
        <div className={styles["image-container"]}>
          <div className={styles["cover"]} />
          <img src={imageURL} className={styles.image} alt="product" />
        </div>

        <div className={styles.info}>
          <div className={styles.content}>
            <div className={styles["left-section"]}>
              {useStreamlinedSubscriptionTemplate ? (
                <div className={styles["streamlined-titles"]}>
                  <div className={styles["streamlined-title"]}>
                    {productTitle}
                  </div>
                  <div className={styles["streamlined-subtitle"]}>
                    {productSubtitle}
                  </div>
                </div>
              ) : (
                <ProductContents
                  product={product}
                  subtitle={subtitle}
                  action={action}
                />
              )}
            </div>
            <div className={`text:regular+ ${styles["flat-months-payment"]}`}>
              {price}
              {emphasizeLending && (
                <div className="text:small color:moderate">
                  {l10n.getString("estimated-payment")}
                </div>
              )}
            </div>
          </div>
        </div>
      </LocalizationProvider>
    </ContainerButton>
  );
};

interface ProductContentsProps {
  product: web.public_.IProduct;
  subtitle: string;
  action: { url: string; text: string };
}

function ProductContents({ product, subtitle, action }: ProductContentsProps) {
  const preview = useSelector((state) => state.shopping.checkoutPreview);

  const price = format.money(product.priceCents, {
    includeCents: true,
  });

  return (
    <>
      <div className={styles["streamlined-titles"]}>
        <div className={styles["streamlined-title"]}>
          {productData[product.type].label
            .split("-")
            .map((word: string) => word.charAt(0) + word.slice(1).toLowerCase())
            .join("-")}
          : {product.title}
        </div>
        <div className={styles["streamlined-subtitle"]}>
          {product.headliner.toUpperCase()}
        </div>
        {subtitle && (
          <div className={`text:small color:moderate ${styles.subtitle}`}>
            {subtitle}
          </div>
        )}
        {preview && <div className={styles["streamlined-price"]}>{price}</div>}
      </div>
      {action && (
        <a
          href={action.url}
          target="_blank"
          rel="noopener noreferrer"
          className="default"
        >
          {action.text}
        </a>
      )}
    </>
  );
}

export default ProductEntry;
